import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowRight, Trash2, Calendar, Building2, User, Search } from 'lucide-react';
import { getServiceCalls, deleteServiceCall } from '../api';
import { toast } from '../hooks/use-toast';
import { ConsolidatedPaidBadge } from '../components/ConsolidatedPaidBadge';
const ServiceCallsList = () => {
  const navigate = useNavigate();
  const [serviceCalls, setServiceCalls] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredCalls = serviceCalls.filter(call => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.trim().toLowerCase();
    return String(call.callNumber ?? '').includes(q) ||
      (call.customerName || '').toLowerCase().includes(q) ||
      (call.branch || '').toLowerCase().includes(q);
  });

  useEffect(() => {
    loadServiceCalls();
  }, []);

  const loadServiceCalls = async () => {
    try {
      const calls = await getServiceCalls();
      setServiceCalls(calls);
    } catch (error) {
      toast({
        title: 'שגיאה',
        description: 'לא ניתן לטעון את הקריאות',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('האם אתה בטוח שברצונך למחוק קריאה זו?')) {
      try {
        await deleteServiceCall(id);
        setServiceCalls(serviceCalls.filter(call => call.id !== id));
        toast({
          title: 'הצלחה',
          description: 'הקריאה נמחקה בהצלחה'
        });
      } catch (error) {
        toast({
          title: 'שגיאה',
          description: 'לא ניתן למחוק את הקריאה',
          variant: 'destructive'
        });
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('he-IL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center" dir="rtl">
        <div className="text-xl text-gray-600">טוען...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-12 px-4" dir="rtl">
      <div className="container mx-auto max-w-6xl">
        {/* Header */}
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">מכללה טכנולוגית</h1>
            <p className="text-gray-600">סך הכל {serviceCalls.length} קריאות</p>
          </div>
        </div>

        {/* Search bar */}
        {serviceCalls.length > 0 && (
          <div className="mb-6 relative max-w-md">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="חיפוש לפי מספר קריאה, שם או סניף..."
              className="w-full pr-10 pl-4 py-3 text-base border-2 border-gray-300 rounded-lg focus:border-indigo-500 focus:outline-none transition-colors bg-white"
              data-testid="service-calls-search"
            />
          </div>
        )}

        {/* Service Calls List */}
        {serviceCalls.length === 0 ? (
          <Card className="shadow-lg">
            <CardContent className="py-16 text-center">
              <p className="text-xl text-gray-500 mb-6">אין קריאות עדיין</p>
              <Button
                onClick={() => navigate('/new-service-call')}
                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white"
              >
                צור קריאה ראשונה
              </Button>
            </CardContent>
          </Card>
        ) : filteredCalls.length === 0 ? (
          <Card className="shadow-lg">
            <CardContent className="py-16 text-center">
              <p className="text-xl text-gray-500">לא נמצאו קריאות התואמות לחיפוש</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {filteredCalls.map((call) => (
              <Card key={call.id} className="shadow-lg hover:shadow-xl transition-shadow border-0">
                <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b">
                  <div className="flex justify-between items-start">
                    <div className="space-y-2 flex-1">
                      <div className="flex items-center gap-3">
                        {call.callNumber != null && (
                          <span className="bg-indigo-600 text-white text-sm font-bold px-3 py-1 rounded-full" data-testid={`service-call-number-${call.id}`}>
                            קריאה #{call.callNumber}
                          </span>
                        )}
                        <User className="w-5 h-5 text-blue-600" />
                        <CardTitle className="text-2xl">{call.customerName}</CardTitle>
                      </div>
                      <div className="flex items-center gap-3 text-gray-600">
                        <Building2 className="w-4 h-4" />
                        <span className="font-medium">סניף: {call.branch}</span>
                      </div>
                      <ConsolidatedPaidBadge invoiceId={call.consolidatedInvoiceId} note={call.consolidatedPaidNote} testId={`servicecall-consolidated-paid-${call.id}`} />
                    </div>
                    <div className="flex flex-col items-end gap-3">
                      <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-200 flex items-center gap-2 px-3 py-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(call.date)}
                      </Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(call.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="space-y-4">
                    <div>
                      <h3 className="font-semibold text-gray-700 text-lg">תיאור התקלה:</h3>
                      <p className="text-gray-600 leading-relaxed bg-gray-50 p-4 rounded-lg mt-2">
                        {call.issueDescription}
                      </p>
                    </div>
                    {call.employeeName && (
                      <p className="text-gray-700" data-testid={`servicecall-list-employee-${call.id}`}><span className="font-semibold">שם העובד:</span> {call.employeeName}</p>
                    )}

                    {/* Work Items Table */}
                    {call.workItems && call.workItems.length > 0 && (
                      <div>
                        <h3 className="font-semibold text-gray-700 text-lg mb-3">פירוט עבודה:</h3>
                        <div className="overflow-x-auto border-2 border-gray-200 rounded-lg">
                          <table className="w-full border-collapse bg-white">
                            <thead>
                              <tr className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white">
                                <th className="border border-gray-300 p-3 text-right">שם חלק</th>
                                <th className="border border-gray-300 p-3 text-center">כמות</th>
                                <th className="border border-gray-300 p-3 text-center">מחיר עלות</th>
                                <th className="border border-gray-300 p-3 text-center">סכום עסקה</th>
                                <th className="border border-gray-300 p-3 text-center">רווח</th>
                              </tr>
                            </thead>
                            <tbody>
                              {call.workItems.map((item, idx) => (
                                <tr key={idx} className="hover:bg-blue-50">
                                  <td className="border border-gray-300 p-3 text-right">{item.partName}</td>
                                  <td className="border border-gray-300 p-3 text-center">{item.quantity}</td>
                                  <td className="border border-gray-300 p-3 text-center text-orange-700 font-semibold">
                                    ₪{parseFloat(item.costPrice || 0).toFixed(2)}
                                  </td>
                                  <td className="border border-gray-300 p-3 text-center text-blue-700 font-semibold">
                                    ₪{parseFloat(item.transactionAmount || 0).toFixed(2)}
                                  </td>
                                  <td className="border border-gray-300 p-3 text-center text-green-700 font-semibold">
                                    ₪{parseFloat(item.profit || 0).toFixed(2)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ServiceCallsList;